import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Landmark, Check, BadgePercent, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg, tl, num } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

export const PackageCard = ({ p, onBuy, cta = "Satın Al" }) => (
  <div data-testid={`package-card-${p.slug || p.id}`} className={cn("glass-card glass-card-hover flex flex-col p-6", p.is_featured && "border-primary/60 shadow-[0_0_40px_hsl(var(--primary)/.18)]")}>
    {p.is_featured && <span className="absolute -top-3 left-6 rounded-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">En Popüler</span>}
    <div className="font-display text-lg font-semibold">{p.name}</div>
    <div className="mt-1 text-sm text-muted-foreground min-h-[2.5rem]">{p.description}</div>
    <div className="mt-5 flex items-baseline gap-2">
      <span className="font-display text-4xl font-bold">{num(p.credits)}</span><span className="text-sm text-muted-foreground">kredi</span>
    </div>
    <div className="mt-5 space-y-2 rounded-xl border border-border bg-muted/30 p-4 text-sm">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-muted-foreground"><CreditCard className="h-4 w-4" />Kart</span>
        <span className="font-semibold" data-testid="package-card-price">
          {p.campaign_active && <s className="mr-2 text-xs text-muted-foreground">{tl(p.list_price)}</s>}{tl(p.card_price)}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-muted-foreground"><Landmark className="h-4 w-4" />Havale/EFT</span>
        <span className="font-semibold text-emerald-500" data-testid="package-iban-price">{tl(p.iban_price)}</span>
      </div>
      {p.iban_discount_rate > 0 && <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-500"><BadgePercent className="h-3.5 w-3.5" />Havalede %{num(p.iban_discount_rate)} indirim</div>}
    </div>
    <Button className="btn-glow mt-6 rounded-xl" onClick={() => onBuy(p)} data-testid={`buy-button-${p.slug || p.id}`}>{cta}</Button>
  </div>
);

export const CheckoutDialog = ({ product, onClose }) => {
  const { settings } = useAuth();
  const nav = useNavigate();
  const [method, setMethod] = useState(null);
  const [busy, setBusy] = useState(false);
  const [iframe, setIframe] = useState(null);
  useEffect(() => { setMethod(settings.card_enabled ? "PAYTR" : "IBAN_TRANSFER"); setIframe(null); }, [product, settings.card_enabled]);
  if (!product) return null;

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/orders", { product_id: product.id, payment_method: method });
      if (method === "PAYTR") setIframe(data.iframe_url);
      else { onClose(); nav(`/siparis/${data.order.id}`); }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const Option = ({ id, icon: Icon, title, price, note, disabled }) => (
    <button type="button" disabled={disabled} onClick={() => setMethod(id)} data-testid={`payment-method-${id === "PAYTR" ? "card" : "iban"}`}
      className={cn("w-full rounded-xl border p-4 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        method === id ? "border-primary bg-primary/10" : "border-border hover:border-primary/50")}>
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-3 font-semibold"><Icon className="h-5 w-5 text-primary" />{title}</span>
        <span className="font-display text-lg font-bold">{price}</span>
      </div>
      <div className="mt-1 pl-8 text-xs text-muted-foreground">{note}</div>
    </button>
  );

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={cn("max-h-[92vh] overflow-y-auto", iframe ? "sm:max-w-2xl" : "sm:max-w-md")} data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{product.name}</DialogTitle>
          <DialogDescription>{num(product.credits)} kredi · Ödeme yöntemini seçin</DialogDescription>
        </DialogHeader>
        {iframe ? (
          <iframe title="PayTR Güvenli Ödeme" src={iframe} className="h-[600px] w-full rounded-xl border-0 bg-white" data-testid="paytr-iframe" />
        ) : (
          <div className="space-y-3">
            <Option id="PAYTR" icon={CreditCard} title="KARTLA ÖDE" price={tl(product.card_price)} disabled={!settings.card_enabled}
              note={settings.card_enabled ? "PayTR güvenli ödeme altyapısı ile anında aktivasyon" : "Kartla ödeme şu anda aktif değil"} />
            <Option id="IBAN_TRANSFER" icon={Landmark} title="IBAN / HAVALE İLE ÖDE" price={tl(product.iban_price)} disabled={!settings.iban_enabled}
              note={`%${num(product.iban_discount_rate)} indirimli · Admin onayı sonrası krediler yüklenir`} />
            <ul className="space-y-1.5 pt-2 text-xs text-muted-foreground">
              <li className="flex gap-2"><Check className="h-3.5 w-3.5 text-emerald-500" />Fiyatlar sunucu tarafından hesaplanır ve siparişe sabitlenir.</li>
              <li className="flex gap-2"><Check className="h-3.5 w-3.5 text-emerald-500" />Ödeme onaylandığında krediler otomatik eklenir.</li>
            </ul>
            <Button className="btn-glow w-full rounded-xl" disabled={busy || !method} onClick={pay} data-testid="checkout-confirm-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{method === "PAYTR" ? "Ödemeye Geç" : "Sipariş Oluştur"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const MediaView = ({ id, type, className, controls = true }) =>
  type?.startsWith("video") ? (
    <video src={`${process.env.REACT_APP_BACKEND_URL}/api/media/${id}`} className={className} controls={controls} muted={!controls} loop playsInline preload="metadata" />
  ) : (
    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/media/${id}`} alt="AI içerik" className={className} loading="lazy" />
  );
